import {
  AiProgressMeasurement,
  AiProgressMetric,
  AiProgressSummary,
} from "./aiProgressSummaryTypes";
import {
  parseAiSummaryNutrition,
  parseAiSummaryRecommendations,
  parseAiSummaryTrainingPlan,
  parseTakeawayItems,
} from "./aiSummaryResponse";

type RecordValue = Record<string, unknown>;
const record = (value: unknown): RecordValue =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as RecordValue)
    : {};
const text = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(text).filter(Boolean) : [];
const number = (value: unknown): number | undefined => {
  if (typeof value !== "number" && !(typeof value === "string" && value.trim()))
    return undefined;
  const result = Number(value);
  return Number.isFinite(result) ? result : undefined;
};

export const unwrapProgressResponse = (payload: unknown): RecordValue => {
  let current = payload;
  for (let depth = 0; depth < 8; depth += 1) {
    if (typeof current === "string") {
      try {
        current = JSON.parse(
          current
            .trim()
            .replace(/^```(?:json)?\s*/i, "")
            .replace(/\s*```$/, "")
        );
      } catch {
        return {};
      }
    }
    const root = record(current);
    const nested = root.data ?? root.response ?? root.result;
    if (nested === undefined) return root;
    current = nested;
  }
  return record(current);
};

const measurements = (
  source: RecordValue,
  fallbackDates: unknown[]
): AiProgressMeasurement[] => {
  const dates = Array.isArray(source.dates) ? source.dates : fallbackDates;
  if (!Array.isArray(source.values)) return [];
  // Pair before filtering: a missing measurement must never shift later dates.
  return source.values.flatMap((rawValue, index) => {
    const value = number(rawValue);
    return value === undefined ? [] : [{ value, date: text(dates[index]) }];
  });
};
export const parseProgressMetric = (
  value: unknown,
  dates: unknown[],
  fallbackLabel = ""
): AiProgressMetric | undefined => {
  const source = record(value);
  const label = text(source.label) || text(source.id) || fallbackLabel;
  const points = measurements(source, dates);
  const interpretation = text(source.interpretation);
  if (!label || (!points.length && !interpretation)) return undefined;
  return {
    id: text(source.id) || label,
    label,
    unit: text(source.unit),
    measurements: points,
    interpretation,
    referenceMin: number(source.reference_min),
    referenceMax: number(source.reference_max),
  };
};
const metrics = (value: unknown, dates: unknown[]): AiProgressMetric[] =>
  Array.isArray(value)
    ? value.flatMap((item) => {
        const result = parseProgressMetric(item, dates);
        return result ? [result] : [];
      })
    : [];

export const parseAiProgressSummaryResponse = (
  payload: unknown
): AiProgressSummary => {
  const root = unwrapProgressResponse(payload);
  const patient = record(root.patient);
  const dates = Array.isArray(patient.test_dates) ? patient.test_dates : [];
  const takeaway = record(root.key_takeaway);
  const age = record(root.biological_age);
  const rmr = record(root.rmr_section);
  const amr = record(root.amr_section);
  const inbody = record(root.inbody_section);
  const recommendations = record(root.recommendations);
  const nutrition = parseAiSummaryNutrition(root.nutrition);
  const recommendedNutrition = parseAiSummaryNutrition(
    recommendations.nutrition
  );
  return {
    patient: {
      name: text(patient.name),
      companyName: text(patient.company_name),
      testDates: strings(dates),
    },
    heroPill: text(root.hero_pill),
    reportTypes: strings(root.report_types),
    keyTakeaway: {
      overallPicture: text(takeaway.overall_picture),
      improved: parseTakeawayItems(takeaway.what_improved),
      attention: parseTakeawayItems(takeaway.what_needs_attention),
    },
    biologicalAge: {
      chronologicalAge: number(age.chronological_age),
      measurements: measurements(age, dates),
    },
    rmr: {
      description: text(root.resting_summary),
      headline: parseProgressMetric(
        rmr.rmr_values,
        dates,
        "Resting metabolic rate"
      ),
      metrics: metrics(rmr.metrics, dates),
    },
    amr: {
      description: text(root.exercise_summary),
      headline: parseProgressMetric(amr.vo2_peak, dates, "VO2 peak"),
      metrics: metrics(amr.metrics, dates),
    },
    inbody: {
      description: text(root.inbody_summary),
      metrics: metrics(inbody.metrics, dates),
    },
    trainingPlan: parseAiSummaryTrainingPlan(
      root.workout ?? recommendations.exercise
    ),
    nutrition: {
      workoutDays: nutrition.workoutDays ?? recommendedNutrition.workoutDays,
      nonWorkoutDays:
        nutrition.nonWorkoutDays ?? recommendedNutrition.nonWorkoutDays,
    },
    recommendations: parseAiSummaryRecommendations(
      recommendations,
      root.services
    ),
    disclaimer: text(root.disclaimer),
  };
};
